export const HOUR_HEIGHT = 48
export const ONE_MINUTE_MS = 60 * 1000
export const MIN_DISPLAY_MS = 30 * 60 * 1000
export const MIN_LANE_WIDTH = 70

/**
 * One lane of the day grid's resource rows, in px.
 *
 * Unrelated to `HOUR_HEIGHT`, which is vertical time in the week column — here time runs
 * horizontally and this is just how tall a booking tile is. It has to clear the tile's two
 * stacked lines (client name over waiver badge) plus the row's 4px of padding and the block's
 * own border, or the badge is cut off along the bottom.
 */
export const DAY_ROW_SLOT_HEIGHT = 56
// Height (px) needed to fit the time row + title row in an event block, including the 4px of
// padding the positioning wrapper adds around it.
export const MIN_TILE_HEIGHT = 52

/**
 * How far right an overlapping tile sits from the one beneath it, in px — which is also how much
 * of the tile beneath stays exposed, so it is sized to carry a time and a client name.
 */
export const OVERLAP_X_STEP = 110
/** How wide the topmost tile of a stack must stay. */
const MIN_TOP_TILE_WIDTH = 130
/** How far down a tile sits when it starts level with the one beneath it, in px. */
export const OVERLAP_Y_STEP = 18

const effectiveEnd = (start, end) => Math.max(end, start + MIN_DISPLAY_MS)

export const buildOverlapClusters = (events) => {
  const sorted = [...events].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const clusters = []
  sorted.forEach((ev) => {
    const start = ev.startMoment?.valueOf() ?? 0
    const end = effectiveEnd(start, ev.endMoment?.valueOf() ?? start)
    const hit = clusters.filter((c) => c.maxEnd > start)
    if (hit.length === 0) {
      clusters.push({ events: [ev], maxEnd: end })
    } else {
      const merged = hit.reduce((acc, c) => {
        clusters.splice(clusters.indexOf(c), 1)
        acc.events.push(...c.events)
        acc.maxEnd = Math.max(acc.maxEnd, c.maxEnd)
        return acc
      }, { events: [ev], maxEnd: end })
      clusters.push(merged)
    }
  })
  return clusters.map((c) => c.events)
}

export const assignColumnsInCluster = (clusterEvents) => {
  const sorted = [...clusterEvents].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const colEnds = []
  const assignments = []
  sorted.forEach((ev) => {
    const startVal = ev.startMoment?.valueOf() ?? 0
    const endVal = effectiveEnd(startVal, ev.endMoment?.valueOf() ?? startVal)
    let colIndex = colEnds.findIndex((colEnd) => colEnd <= startVal)
    if (colIndex === -1) { colIndex = colEnds.length; colEnds.push(endVal) }
    else colEnds[colIndex] = endVal
    assignments.push({ ev, colIndex })
  })
  const totalCols = Math.max(colEnds.length, 1)
  // A tile only needs the cascade's step down when it starts level with something under it —
  // "level" meaning the grid would draw the two tops less than one step apart. Anything later
  // already sits lower on its own.
  return assignments.map(({ ev, colIndex }, index) => {
    const startVal = ev.startMoment?.valueOf() ?? 0
    const lowerStart = assignments.reduce((latest, other, otherIndex) => {
      if (otherIndex === index || other.colIndex >= colIndex) return latest
      return Math.max(latest, other.ev.startMoment?.valueOf() ?? 0)
    }, Number.NEGATIVE_INFINITY)
    const gapPx = ((startVal - lowerStart) / ONE_MINUTE_MS) * (HOUR_HEIGHT / 60)
    return { ...ev, colIndex, totalCols, isFlushWithLower: gapPx < OVERLAP_Y_STEP }
  })
}

export const computeEventColumns = (events) => buildOverlapClusters(events).flatMap(assignColumnsInCluster)

export const getEventPosition = (ev, startHour, endHour) => {
  const { startMoment, endMoment } = ev
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null
  const gridEndMins = endHour != null ? (endHour - startHour + 1) * 60 : Infinity
  const startMins = (startMoment.hour() - startHour) * 60 + startMoment.minute()
  const endMins = (endMoment.hour() - startHour) * 60 + endMoment.minute()
  const clampedStart = Math.max(0, startMins)
  const clampedEnd = Math.min(gridEndMins, Math.max(endMins, startMins + 30))
  if (clampedEnd <= clampedStart) return null
  // A booking shorter than half an hour — and the calendar does carry 7:00-7:00 ones — is under
  // 24px at this scale, which cuts its own two lines of text in half. Every tile gets the same
  // floor as a multi-day one: enough for the time and title rows, at the cost of a very short
  // booking drawing slightly past its end.
  const height = (clampedEnd - clampedStart) * (HOUR_HEIGHT / 60)
  return {
    top: clampedStart * (HOUR_HEIGHT / 60),
    height: Math.max(height, MIN_TILE_HEIGHT),
  }
}

/**
 * Places one tile of an overlapping cluster as a cascade, the way the design draws it.
 *
 * Each tile keeps the column's right edge and steps right of the one below by `OVERLAP_X_STEP`, so
 * the topmost is widest and every tile under it keeps a full step of itself exposed — its payment
 * bar, its time and its client name. Splitting the column into equal lanes instead would leave
 * every booking narrow for its whole length and lose the layered look entirely; the readability
 * comes from `getDayMinWidth` widening the day to fit the stack, not from breaking the overlap.
 *
 * The step down applies only to a tile that starts level with the one beneath it (`isFlushWithLower`
 * from `assignColumnsInCluster`). A booking that starts later is already lower on the grid and
 * needs no nudge — stepping it again would move it off its own hour. That step is taken out of the
 * tile's height, not added to it: a tile must never finish below its own end time, or a 4-6 AM
 * booking is drawn over the 6 AM one and the grid lies about when the day is free. A tile that
 * cannot spare the height keeps it instead and settles for the sideways step — a one-hour booking
 * is only `MIN_TILE_HEIGHT` tall to begin with, and trimming it would cost it its second line.
 *
 * @param {Object} ev an event carrying `colIndex`/`totalCols` from `computeEventColumns`
 * @param {number} height the tile's height in px, from `getEventPosition`
 * @returns {{left: number, width: string, top: number, height: number, zIndex: number,
 *   isCascaded: boolean}} inline style values; `top` is an offset to add to the tile's own position
 */
export const getOverlapLayout = (ev, height = 0) => {
  const depth = ev?.colIndex || 0
  if (depth === 0) return { left: 0, width: "100%", top: 0, height, zIndex: 2, isCascaded: false }
  const left = depth * OVERLAP_X_STEP
  const offset = ev?.isFlushWithLower
    ? Math.min(depth * OVERLAP_Y_STEP, Math.max(height - MIN_TILE_HEIGHT, 0))
    : 0
  return {
    left,
    width: `calc(100% - ${left}px)`,
    top: offset,
    height: height - offset,
    zIndex: 2 + depth,
    isCascaded: true
  }
}

export const getLanesForDay = (eventsWithCols) =>
  eventsWithCols.length > 0 ? Math.max(...eventsWithCols.map((e) => e.totalCols)) : 1

/**
 * How wide a day column has to be for its busiest hour, in px.
 *
 * A cascade only reads if the column can afford one `OVERLAP_X_STEP` for every tile buried under
 * the top one, plus `MIN_TOP_TILE_WIDTH` for the top one itself. A day with no overlaps keeps the
 * old minimum, so only the busy days grow and the week scrolls sideways just for those.
 */
export const getDayMinWidth = (lanes) =>
  lanes <= 1 ? 120 : (lanes - 1) * OVERLAP_X_STEP + MIN_TOP_TILE_WIDTH

/** The strip's own row height, independent of HOUR_HEIGHT — it holds one 24px pill plus padding. */
export const MULTI_DAY_ROW_HEIGHT = 40

/** A multi-day booking is the same trip on every day it covers, so it is keyed once. */
const runKey = (booking) => booking?.uuid || booking?.id

/**
 * Lays the visible window's multi-day bookings out as one pill per trip.
 *
 * The store expands a multi-day booking into one copy per day it covers, which in a time grid
 * would draw the same trip up to seven times — once in each column, at its start time. The design
 * lifts them out of the grid entirely and gives them a strip of their own above it, where each
 * trip is a single bar across the columns it spans.
 *
 * A trip that began before the window, or runs past it, says so with a chevron rather than being
 * clipped silently: `booked_dates` is the trip's whole span, so anything outside `dayKeys` means
 * there is more of it off-screen.
 *
 * Runs are packed into the fewest lanes that keep them from overlapping, longest first — the same
 * rule the month grid's bars use.
 *
 * @param {string[]} dayKeys the visible days in order, as "YYYY-MM-DD"
 * @param {Object[]} bookings every booking in the window, single- and multi-day alike
 * @returns {{runs: Object[], laneCount: number}} each run carries `startIndex`, `span`, `lane`
 *   and the two `continues*` flags
 */
export const buildMultiDayRuns = (dayKeys = [], bookings = []) => {
  const runs = new Map()

  bookings.forEach((booking) => {
    if (!booking?.multi_day) return
    const index = dayKeys.indexOf(booking.date)
    if (index === -1) return
    const key = runKey(booking)
    const run = runs.get(key)
    if (run) {
      run.startIndex = Math.min(run.startIndex, index)
      run.endIndex = Math.max(run.endIndex, index)
      return
    }
    const bookedDates = Array.isArray(booking.booked_dates) ? [...booking.booked_dates].sort() : []
    runs.set(key, {
      booking,
      startIndex: index,
      endIndex: index,
      continuesBefore: Boolean(bookedDates.length) && bookedDates[0] < dayKeys[0],
      continuesAfter: Boolean(bookedDates.length) && bookedDates[bookedDates.length - 1] > dayKeys[dayKeys.length - 1]
    })
  })

  // Longest first at equal starts, so a long trip takes the top lane and short ones tuck under it.
  const ordered = [...runs.values()].sort(
    (left, right) => left.startIndex - right.startIndex || right.endIndex - left.endIndex
  )
  const laneEnds = []

  ordered.forEach((run) => {
    let lane = laneEnds.findIndex((lastIndex) => lastIndex < run.startIndex)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(-1)
    }
    laneEnds[lane] = run.endIndex
    run.lane = lane
    run.span = run.endIndex - run.startIndex + 1
  })

  return { runs: ordered, laneCount: laneEnds.length }
}
